import React from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {useSelector} from 'react-redux';

import {useAcceptance} from 'app/acceptance/api';
import {PendingAcceptanceInfoBar} from 'app/acceptance/components/PendingAcceptanceInfoBar';
import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import VersionInfoBar from 'app/components/general/VersionInfoBar';
import {showNoApprovedVersionsAlertBoxIfNotApproved} from 'app/general/structures/document-alerts/NoApprovedVersionsAlertBox';
import {MainContentContainer} from 'app/general/structures/MainContentContainer';
import {SPPage} from 'app/general/structures/SPPage';
import {VideoManagerProvider} from 'app/general/VideoManagerProvider';
import {
  usePolicyByHashid,
  usePolicyVersionById,
  usePolicyVersions,
} from 'app/policies/api';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {toggleProcedureAdvancedMode} from 'app/procedures/actions';
import {ProcedureHeaderOptionsDropdown} from 'app/procedures/components/ProcedureHeaderOptionsDropdown';
import {ReviewReminderWarning} from 'app/review/components/ReviewReminderWarning';
import {useAccountSettingsState} from 'app/settings/api';
import {SignoffForm} from 'app/signoffs/components/SignoffForm';
import {SignoffRequiredInfoBar} from 'app/signoffs/components/SignoffRequiredInfoBar';
import {
  allRelatedMatch,
  modelIdMatch,
  useModelChangeSubscription,
} from 'app/subscriptions';
import {getPolicyName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {whenVisible} from 'app/utils/page-visibility';
import {useAsyncActions} from 'app/utils/redux';
import {isLastVersion} from 'app/version/utils';

import {PolicyDiffer} from './PolicyDiffer';
import {PolicyPageSidebar} from './PolicyPageSidebar';

import {getContentFromPolicy} from '../PolicyEditPage/utils';
import {ViewPolicyContent} from '../ViewPolicyContent';
import {ViewPolicyMetadata} from '../ViewPolicyMetadata';

import type {PolicyRouterParamsType, PolicyRouteType} from 'app/policies/types';
import type {ModelChangeMessageType} from 'app/subscriptions';
import type {WithRouterProps} from 'react-router';

type Props = WithRouterProps<PolicyRouterParamsType> & {
  route: PolicyRouteType;
};

export const PolicyDetailPage = ({router, route, params, location}: Props) => {
  const settings = useAccountSettingsState().data!;
  const deletingDocument = useSelector(
    (state) => state.toasts.isDeletingDocument
  );

  const {data: policy, refetch: refetchPolicy} = usePolicyByHashid(
    params.hashid
  );
  const {data: version, refetch: refetchVersion} = usePolicyVersionById(
    params.versionid ? params.versionid : skipToken,
    {
      selectFromResult: ({data, status}) =>
        status !== 'uninitialized' ? {data} : {data: undefined},
      refetchOnMountOrArgChange: false,
    }
  );
  const {data: versions, refetch: refetchVersions} = usePolicyVersions(
    policy
      ? {
          objectId: policy.id,
        }
      : skipToken
  );
  const {data: acceptances, refetch: refetchAcceptances} = useAcceptance(
    policy ? {object_pk: policy.id, content_type: 'policy'} : skipToken
  );

  const acceptance = acceptances?.results[0];

  const advancedMode = useSelector((state) =>
    state.proceduresPage.get('procedureAdvancedMode')
  );

  const refetchData = whenVisible(() => {
    refetchPolicy();
    refetchVersion();
  });

  const actions = useAsyncActions({
    toggleProcedureAdvancedMode,
  });

  const content = getContentFromPolicy(version ?? policy);
  useModelChangeSubscription(
    (message: ModelChangeMessageType, {runWhenAny}) => {
      if (deletingDocument) {
        return;
      }
      if (policy) {
        const expectedRelated = {
          content_type: 'policy',
          id: policy?.id,
        };
        runWhenAny(
          message,
          [
            modelIdMatch('policy', policy.id),
            allRelatedMatch('policyversion', expectedRelated),
            allRelatedMatch('review', expectedRelated),
          ],
          refetchData
        );
      }
      if (version != null) {
        const expectedRelatedVersion = {
          content_type: 'policyversion',
          id: version.id,
        };
        runWhenAny(
          message,
          [allRelatedMatch('acceptance', expectedRelatedVersion)],
          refetchAcceptances
        );
      }
    },
    [deletingDocument, params, policy]
  );

  if (policy == null || content == null) {
    return null;
  }

  const actionButtonsProps = {
    contentType: 'policy' as const,
    canChange: policy.can_change,
    refreshData: refetchData,
    additionalContent: () => (
      <ProcedureHeaderOptionsDropdown
        advancedModeEnabled={advancedMode}
        toggleAdvancedMode={actions.toggleProcedureAdvancedMode}
        numCollapsedSteps={0}
        onToggleAllStepsCollapse={() => null}
        stepsLength={0}
        hideAdvancedMode={version != null}
      />
    ),
  };

  return (
    <PolicyDiffer
      location={location}
      content={content}
      versions={versions?.results ?? []}
      versionAvailable={version != null}
    >
      {(contentWithDiff, showingDiff, toggleDiff) => (
        <SPPage
          pageTitle={policy.name}
          horizontalPadding={false}
          breadcrumbProps={{
            docName: getPolicyName(policy),
            version: version,
          }}
          actionArea={
            version != null ? (
              <GenericDocumentActionButtons
                version={version}
                isLastVersion={isLastVersion(version, versions?.results ?? [])}
                pageContext="view-version"
                document={policy}
                {...actionButtonsProps}
              />
            ) : (
              <GenericDocumentActionButtons
                pageContext="view-published"
                document={policy}
                {...actionButtonsProps}
              />
            )
          }
          sidebar={
            <PolicyPageSidebar
              policy={policy}
              versionId={params.versionid}
              acceptance={acceptance}
              fetchPolicy={() => {
                refetchPolicy();
                return Promise.resolve();
              }}
              fetchPolicyVersions={() => {
                refetchVersions();
                return Promise.resolve();
              }}
              version={version}
              versions={versions?.results || []}
              route={route}
              router={router}
              location={location}
            />
          }
          versionbar={
            <>
              {version != null ? (
                <VersionInfoBar
                  objectTypeName={gt('policy')}
                  version={version}
                  showingDiff={showingDiff}
                  toggleDiff={toggleDiff}
                />
              ) : null}
              {!route?.props.showAccept && acceptance != null ? (
                <PendingAcceptanceInfoBar
                  document={policy}
                  acceptance={acceptance}
                  location={location}
                  openedVersion={version}
                />
              ) : null}
              {version == null ? (
                <SignoffRequiredInfoBar
                  contentType="policy"
                  objectId={policy.id}
                  viewChangesURL={policy.current_version.html_url}
                />
              ) : null}
            </>
          }
        >
          <DocumentPresenceMembers
            contentType="policy"
            objectId={policy.id}
            pageContext="view"
          />
          <VideoManagerProvider>
            <ViewPolicyMetadata
              policyContent={contentWithDiff}
              policy={policy}
              advancedModeEnabled={advancedMode}
              warningComponent={
                <div>
                  {showNoApprovedVersionsAlertBoxIfNotApproved(
                    'policy',
                    policy.approved_at
                  )}
                  <ReviewReminderWarning
                    contentType="policy"
                    versionId={policy.current_version.id}
                    versionContentType="policyversion"
                    canReview={policy.can_edit}
                    onReview={refetchPolicy}
                    reviewDate={policy.next_review_at}
                  />
                </div>
              }
            />

            <MainContentContainer>
              <ViewPolicyContent
                editable={false}
                content={contentWithDiff}
                mediaURL={settings.MEDIA_URL}
                mediaURLRead={settings.MEDIA_URL_READ}
              />
              {version == null ? (
                <SignoffForm
                  objectId={policy.id}
                  contentType="policy"
                  versionId={policy.current_version.id}
                  versionContentType="policyversion"
                  objectName={policy.name}
                />
              ) : null}
            </MainContentContainer>
          </VideoManagerProvider>
        </SPPage>
      )}
    </PolicyDiffer>
  );
};
